import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { map } from 'lodash';
import { I18nReactContext } from '../../../contexts/I18nContext';
import { CompanyReactContext } from '../../../contexts/CompanyContext';
import { DataTable } from '../../DataTable/DataTable';
import { Button } from '../../Form/Button/Button';
import { buttonColors } from '../../Pickers/InputTheme';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { formatPhoneForDisplay } from '../../../Utils/functions';
import './ImportUsersPreview.scss';

/**
 * Component for previewing matched and unmatched users from a CSV/Excel import.
 * Displays two sections: matched users (ready to add) and unmatched rows (for review).
 * Allows user to confirm adding matched users or cancel the import.
 *
 * @param {Object} props - Component props
 * @param {Array<{id: string, firstName: string, lastName: string, phone: string, email: string}>} props.matchedUsers - Users matched from the import file
 * @param {Array<{rowIndex: number, phone: string, firstName: string, lastName: string}>} props.unmatchedRows - Rows that didn't match any user
 * @param {Function} props.onConfirm - Callback when user confirms adding matched users. Called with array of user IDs.
 * @param {Function} props.onCancel - Callback when user cancels the import
 * @param {boolean} [props.isLoading=false] - Whether the component is in loading state
 * @returns {JSX.Element} The ImportUsersPreview component
 */
export const ImportUsersPreview = ({
    matchedUsers,
    unmatchedRows,
    onConfirm,
    onCancel,
    isLoading = false
}) => {
    const { i18n } = useContext(I18nReactContext);
    const companyContext = useContext(CompanyReactContext);

    /**
     * Column configuration for matched users DataTable
     * Matches the columns in ImportUsers for consistency
     */
    const matchedUsersColumns = useMemo(() => [
        {
            key: 'firstName',
            label: i18n.translate('importUsersModal.users.firstName'),
            width: 150
        },
        {
            key: 'lastName',
            label: i18n.translate('importUsersModal.users.lastName'),
            width: 150
        },
        {
            key: 'phone',
            label: i18n.translate('importUsersModal.users.phone'),
            width: 170,
            render: ({ row }) => formatPhoneForDisplay({phone: row.phone, countryCode: companyContext.countryCode})
        },
        {
            key: 'email',
            label: i18n.translate('importUsersModal.users.email'),
            width: 300
        },
        {
            key: 'idNumber',
            label: i18n.translate('importUsersModal.users.idNumber'),
            width: 120,
            render: ({ row }) => row.idNumber || '-'
        },
        {
            key: 'employeeId',
            label: i18n.translate('importUsersModal.users.employeeId'),
            width: 120,
            render: ({ row }) => row.employeeId || '-'
        }
    ], [i18n, companyContext.countryCode]);

    /**
     * Column configuration for unmatched rows DataTable
     * Matches the columns in ImportUsers for consistency
     */
    const unmatchedRowsColumns = useMemo(() => [
        {
            key: 'firstName',
            label: i18n.translate('importUsersModal.users.firstName'),
            width: 150,
            render: ({ row }) => row.firstName || '-'
        },
        {
            key: 'lastName',
            label: i18n.translate('importUsersModal.users.lastName'),
            width: 150,
            render: ({ row }) => row.lastName || '-'
        },
        {
            key: 'phone',
            label: i18n.translate('importUsersModal.users.phone'),
            width: 170,
            render: ({ row }) => row.phone || '-'
        },
        {
            key: 'email',
            label: i18n.translate('importUsersModal.users.email'),
            width: 300,
            render: ({ row }) => row.email || '-'
        },
        {
            key: 'idNumber',
            label: i18n.translate('importUsersModal.users.idNumber'),
            width: 120,
            render: ({ row }) => row.idNumber || '-'
        },
        {
            key: 'employeeId',
            label: i18n.translate('importUsersModal.users.employeeId'),
            width: 120,
            render: ({ row }) => row.employeeId || '-'
        }
    ], [i18n]);

    /**
     * Handles confirmation - extracts user IDs and calls onConfirm
     */
    const handleConfirm = () => {
        const userIds = map(matchedUsers, 'id');
        onConfirm(userIds);
    };

    const hasMatchedUsers = matchedUsers && matchedUsers.length > 0;
    const hasUnmatchedRows = unmatchedRows && unmatchedRows.length > 0;

    if (isLoading) {
        return (
            <div className="ImportUsersPreview" data-testid="import-users-preview">
                <div className="ImportUsersPreview__loading">
                    <LoadingSpinner />
                    <span className="ImportUsersPreview__loading-text">
                        {i18n.translate('signDocModal.importUsers.processingFile')}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="ImportUsersPreview" data-testid="import-users-preview">
            {/* Matched Users Section */}
            <div
                className="ImportUsersPreview__section ImportUsersPreview__section--matched"
                data-testid="import-users-matched-section"
                aria-label={i18n.translate('signDocModal.importUsers.matchedUsers')}
            >
                <div className="ImportUsersPreview__section-header">
                    <CheckCircleIcon className="ImportUsersPreview__section-icon ImportUsersPreview__section-icon--success" />
                    <span>{i18n.translate('signDocModal.importUsers.matchedUsers')}</span>
                    <span className="ImportUsersPreview__section-count">
                        ({matchedUsers.length})
                    </span>
                </div>

                {hasMatchedUsers ? (
                    <div className="ImportUsersPreview__table-container">
                        <DataTable
                            columns={matchedUsersColumns}
                            data={matchedUsers}
                            showIndex={true}
                            indexLabel="#"
                            getRowKey={(row) => row.id}
                        />
                    </div>
                ) : (
                    <div className="ImportUsersPreview__empty-message">
                        {i18n.translate('signDocModal.importUsers.noMatchesFound')}
                    </div>
                )}
            </div>

            {/* Unmatched Rows Section - Only show if there are unmatched rows */}
            {hasUnmatchedRows && (
                <div
                    className="ImportUsersPreview__section ImportUsersPreview__section--unmatched"
                    data-testid="import-users-unmatched-section"
                    aria-label={i18n.translate('signDocModal.importUsers.unmatchedRows')}
                >
                    <div className="ImportUsersPreview__section-header">
                        <WarningAmberIcon className="ImportUsersPreview__section-icon ImportUsersPreview__section-icon--warning" />
                        <span>{i18n.translate('signDocModal.importUsers.unmatchedRows')}</span>
                        <span className="ImportUsersPreview__section-count">
                            ({unmatchedRows.length})
                        </span>
                    </div>

                    <div className="ImportUsersPreview__table-container">
                        <DataTable
                            columns={unmatchedRowsColumns}
                            data={unmatchedRows}
                            showIndex={true}
                            indexLabel="#"
                            getRowKey={(row, index) => `unmatched-${index}`}
                        />
                    </div>

                    <div className="ImportUsersPreview__hint">
                        {i18n.translate('signDocModal.importUsers.unmatchedRowsHint')}
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="ImportUsersPreview__actions">
                <Button
                    color={buttonColors.GREY}
                    onClick={onCancel}
                    text={i18n.translate('general.cancel')}
                    data-testid="import-users-cancel-button"
                />
                <Button
                    color={buttonColors.YELLOW}
                    onClick={handleConfirm}
                    disabled={!hasMatchedUsers}
                    text={i18n.translate('signDocModal.importUsers.addUsers', {
                        context: { count: matchedUsers.length }
                    })}
                    data-testid="import-users-confirm-button"
                />
            </div>
        </div>
    );
};

ImportUsersPreview.propTypes = {
    matchedUsers: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
        idNumber: PropTypes.string,
        employeeId: PropTypes.string
    })).isRequired,
    unmatchedRows: PropTypes.arrayOf(PropTypes.shape({
        rowIndex: PropTypes.number,
        phone: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        email: PropTypes.string,
        idNumber: PropTypes.string,
        employeeId: PropTypes.string
    })).isRequired,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isLoading: PropTypes.bool
};

export default ImportUsersPreview;
